import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";

import { Finding } from "stores/FindingStore";
import { useIgnoreStore, RuleType } from "stores/IgnoreStore";

import { FOTS } from "utility/typescript";

import { IgnoreTypeData } from "./IgnoreTypePicker";
import RegexPreviewTable from "./RegexPreviewTable";

// -----------------------------------------------------------------------------

const RegexInput = memo(function RegexInput({
  data,
  setData,
  finding,
}: RegexInputProps): JSX.Element {
  const { ignoreType, assetRegex, regexError } = data;

  const [, ignoreActions] = useIgnoreStore();

  const [showPreview, setShowPreview] = useState(false);
  const [openTable, setOpenTable] = useState(false);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(false);

  const onPreview = () => {
    setLoading(true);

    if (assetRegex === "") {
      setData({
        ...data,
        regexError: true,
      });
      setLoading(false);
      return;
    }

    const previewData = {
      uuid: finding.uuid,
      ignoreType,
      ruleType: "asset" as RuleType,
      ignoreReason: "",
      assetRegex,
      onSuccess,
      onError,
    };

    ignoreActions.previewIgnore(previewData);
  };

  const onSuccess = (findings: Finding[]) => {
    setFindings(findings);
    setShowPreview(true);
    setLoading(false);
  };

  const onError = () => {
    setLoading(false);
  };

  return (
    <>
      <Typography
        variant="body1"
        sx={{ mt: 1, mb: 2, color: "text.secondary" }}
      >
        You are choosing to ignore all current and future findings matching the
        regex below
      </Typography>
      <TextField
        id="ignore-rule-regex-text-field"
        label="Asset regex"
        variant="outlined"
        fullWidth
        required
        error={regexError}
        value={assetRegex}
        onChange={(e: FOTS) => {
          setData({
            ...data,
            assetRegex: e?.target?.value,
            regexError: false,
          });
          setShowPreview(false);
        }}
        helperText={regexError ? "Required" : ""}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <LoadingButton
                loading={loading}
                size="small"
                variant="text"
                onClick={onPreview}
              >
                Preview
              </LoadingButton>
            </InputAdornment>
          ),
        }}
      />
      {showPreview && (
        <>
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setOpenTable(!openTable)}
          >
            {openTable && (
              <ArrowDropUp
                fontSize="small"
                sx={{ color: "action.active", mr: 0.5 }}
              />
            )}
            {!openTable && (
              <ArrowDropDown
                fontSize="small"
                sx={{ color: "action.active", mr: 0.5 }}
              />
            )}
            <Typography variant="body1">
              {findings.length} {findings.length > 1 ? "assets" : "asset"} found
            </Typography>
          </Box>
          <Collapse in={openTable} sx={{ mt: 1 }} mountOnEnter unmountOnExit>
            <RegexPreviewTable findings={findings} />
          </Collapse>
        </>
      )}
    </>
  );
});

export default RegexInput;

// -----------------------------------------------------------------------------

interface RegexInputProps {
  data: IgnoreTypeData;
  setData: (data: IgnoreTypeData) => void;
  finding: Finding;
}
